<template>
  <div :class="['si-page', { 'locale-ja': locale === 'ja' }]">
    <section class="hero">
      <img :src="getImageUrl(pr.heroImage)" :alt="pr.heroTitle" class="hero-bg">
      <div class="hero-mask"></div>
      <div class="inner hero-inner">
        <SIBreadcrumb
          :breadcrumb="pr.common.breadcrumb"
          :locale="locale"
          :show-products="false"
          :current="pr.heroTitle"
        />
        <h1>{{ pr.heroTitle }}</h1>
        <p v-if="pr.heroSubtitle" class="hero-subtitle">{{ pr.heroSubtitle }}</p>
        <p class="hero-tagline">{{ pr.heroTagline }}</p>
      </div>
    </section>

    <section class="intro">
      <div class="inner narrow">
        <p
          v-viewportChanged="showView"
          class="hidden-element"
          v-for="(line, index) in pr.intro"
          :key="`intro-${index}`"
        >
          {{ line }}
        </p>
      </div>
    </section>

    <section class="block light-bg">
      <div class="inner">
        <SISectionTitle :title="pr.common.coreModules" :subtitle="pr.common.coreModulesEN" />
        <div v-viewportChanged="showView" class="hidden-element card-grid three">
          <article v-for="module in pr.modules" :key="module.title" class="card">
            <div class="icon-wrap">
              <img :src="getImageUrl(module.icon)" :alt="module.title">
            </div>
            <h3>{{ module.title }}</h3>
            <p>{{ module.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="block">
      <div class="inner">
        <SISectionTitle :title="pr.common.scenarios" :subtitle="pr.common.scenariosEn" />
        <div v-viewportChanged="showView" :class="['hidden-element card-grid', scenarioImage ? 'three' : 'four']">
          <article v-for="scenario in pr.scenarios" :key="scenario.title" class="card scenario-card">
            <template v-if="scenarioImage">
              <div class="scene-image-wrap">
                <img :src="getImageUrl(scenario.image)" :alt="scenario.title" class="scene-image">
              </div>
            </template>
            <template v-else>
              <div class="icon-wrap circle">
                <img :src="getImageUrl(scenario.icon)" :alt="scenario.title">
              </div>
            </template>
            <h3>{{ scenario.title }}</h3>
            <p>{{ scenario.desc }}</p>
            <ul v-if="scenario.cases" class="case-list">
              <li v-for="line in scenario.cases" :key="line">{{ line }}</li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <slot name="afterScenarios"></slot>

    <section v-if="pr.benefits" class="block dark-bg">
      <div class="inner">
        <SISectionTitle :title="pr.common.benefits" :subtitle="pr.common.benefitsEn" light />
        <div v-viewportChanged="showView" class="hidden-element card-grid four">
          <article
            v-for="benefit in pr.benefits"
            :key="benefit.title"
            :class="['benefit-card', { 'benefit-card-center': benefitCenter }]"
          >
            <template v-if="benefitMode === 'icon'">
              <div class="benefit-icon-wrap">
                <img :src="getImageUrl(benefit.icon)" :alt="benefit.title">
              </div>
            </template>
            <template v-else>
              <div class="benefit-number">
                <span
                  v-for="(part, idx) in formatBenefitNumber(benefit.number)"
                  :key="`${benefit.title}-num-${idx}`"
                  :class="['num-part', part.type]"
                >
                  {{ part.text }}
                </span>
              </div>
            </template>
            <h3>{{ benefit.title }}</h3>
            <p>{{ benefit.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <slot name="afterBenefits"></slot>

    <section class="cta">
      <div class="inner cta-inner">
        <SISectionTitle :title="pr.common.cta" />
        <BaseButton tag="router-link" variant="dark" size="hero" :to="contactLink">
          <span v-viewportChanged="showView" class="hidden-element">{{ contactText }}</span>
        </BaseButton>
      </div>
    </section>
  </div>
</template>

<script>
import BaseButton from '@/components/common/BaseButton.vue';
import SISectionTitle from '@/components/si/SISectionTitle.vue';
import SIBreadcrumb from '@/components/si/SIBreadcrumb.vue';
import formatBenefitNumber from '@/utils/formatBenefitNumber';

export default {
  name: 'SIDetailPage',
  components: {
    BaseButton,
    SISectionTitle,
    SIBreadcrumb,
  },
  props: {
    pr: {
      type: Object,
      required: true,
    },
    locale: {
      type: String,
      default: '',
    },
    getImageUrl: {
      type: Function,
      required: true,
    },
    contactText: {
      type: String,
      default: '联系我们',
    },
    benefitMode: {
      type: String,
      default: 'number',
    },
    scenarioImage: {
      type: Boolean,
      default: false,
    },
    benefitCenter: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    contactLink() {
      return {
        name: 'contact-us',
        params: {
          lang: this.locale || undefined,
        },
      };
    },
  },
  methods: {
    formatBenefitNumber,
  },
};
</script>

<style lang="scss" scoped>
.si-page {
  background: #ffffff;
}

.si-page.locale-ja {
  h1,
  h2,
  h3,
  h4,
  p,
  li,
  a,
  strong,
  span,
  small,
  td,
  th {
    word-break: normal;
    overflow-wrap: break-word;
    line-break: strict;
  }
}

.inner {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}

.narrow {
  max-width: 800px;
}

.hero {
  position: relative;
  min-height: 420px;
  display: flex;
  align-items: flex-end;

  .hero-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .hero-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.64);
  }

  .hero-inner {
    position: relative;
    z-index: 1;
    padding-bottom: 54px;

    h1 {
      margin: 20px 0 0;
      font-size: 34px;
      line-height: 46px;
      color: #ffffff;
    }

    .hero-subtitle {
      margin: 10px 0 0;
      font-size: 16px;
      line-height: 24px;
      color: #a7aeb1;
    }

    .hero-tagline {
      margin: 10px 0 0;
      max-width: 680px;
      font-size: 16px;
      line-height: 28px;
      color: rgba(255, 255, 255, 0.9);
    }
  }
}

.intro {
  padding: 64px 0;

  p {
    margin: 0 0 16px;
    font-size: 16px;
    line-height: 30px;
    color: #212121;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.block {
  padding: 82px 0;

  &.light-bg {
    background: #f7f9fa;
  }

  &.dark-bg {
    background: #222222;
  }
}

.card-grid {
  display: grid;
  gap: 24px;

  &.three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &.four {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.card {
  border: 1px solid #e4e9eb;
  background: #ffffff;
  padding: 24px;
  display: flex;
  flex-direction: column;

  .icon-wrap {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }

    &.circle {
      width: 56px;
      height: 56px;
      border-radius: 9999px;
      background: #f1f3f4;
      margin: 0 auto 14px;

      img {
        width: 28px;
        height: 28px;
      }
    }
  }

  h3 {
    margin: 14px 0 8px;
    font-size: 18px;
    line-height: 27px;
    color: #212121;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 24px;
    color: #666666;
    flex: 1;
  }
}

.scenario-card {
  .scene-image-wrap {
    margin: -24px -24px 16px;
    height: 160px;

    .scene-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .case-list {
    margin: 14px 0 0;
    padding: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 12px;
      margin-bottom: 8px;
      font-size: 14px;
      line-height: 22px;
      color: #666666;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 9px;
        width: 5px;
        height: 5px;
        border-radius: 9999px;
        background: #1565c0;
      }
    }
  }
}

.benefit-card {
  background: #303233;
  padding: 24px;

  .benefit-number {
    color: #3784e6;
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;

    .num-part {
      display: inline-block;
    }

    .num-part.plus,
    .num-part.minus {
      margin-inline: 0.2em;
    }

    .num-part.minus {
      transform: translateY(-0.1em);
    }
    .num-part.plus {
      transform: translateY(-0.2em);
    }
  }

  .benefit-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 9999px;
    background: #464849;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  }

  h3 {
    margin: 12px 0 8px;
    color: #ffffff;
    font-size: 16px;
    line-height: 24px;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 14px;
    line-height: 22px;
  }
}

.benefit-card-center {
  text-align: center;

  .benefit-icon-wrap,
  .benefit-number {
    margin: 0 auto;
  }
}

.cta {
  background: #f1f3f4;
  padding: 66px 0;

  .cta-inner {
    text-align: center;
  }
}

@media only screen and (max-width: 1024px) {
  .card-grid {
    &.three,
    &.four {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

@media only screen and (max-width: 768px) {
  .inner {
    padding: 0 12px;
  }

  .hero {
    min-height: 340px;

    .hero-inner {
      padding-bottom: 28px;

      h1 {
        margin-top: 16px;
        font-size: 26px;
        line-height: 36px;
      }

      .hero-tagline {
        font-size: 14px;
        line-height: 24px;
      }
    }
  }

  .intro {
    padding: 42px 0;

    p {
      text-align: left;
      font-size: 14px;
      line-height: 24px;
    }
  }

  .block {
    padding: 56px 0;
  }

  .card-grid {
    &.three,
    &.four {
      grid-template-columns: 1fr;
      gap: 14px;
    }
  }

  .cta {
    padding: 54px 0;
  }
}
</style>
